import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import healthService from '../../services/healthService';
import studentsService from '../../services/studentsService';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, Plus, PencilSimple, Trash, Check, X as XIcon, CaretDown, CaretRight, Pill } from '@phosphor-icons/react';

const emptyForm = {
  student_id: '',
  visit_date: new Date().toISOString().split('T')[0],
  symptoms: '',
  treatment_given: '',
  referred_out: false,
  referred_to: '',
  attended_by: '',
  notes: '',
};

const emptyMedForm = {
  medication_name: '',
  dose: '',
  route: 'oral',
  time_given: new Date().toISOString().slice(0, 16),
  administered_by: '',
  notes: '',
};

const MED_ROUTES = ['oral', 'topical', 'injection', 'inhaled'];

function ClinicVisitsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  // Medications (MAR) sub-list, per visit
  const [expandedVisitId, setExpandedVisitId] = useState(null);
  const [medicationsByVisit, setMedicationsByVisit] = useState({});
  const [loadingMedications, setLoadingMedications] = useState(false);
  const [showMedModal, setShowMedModal] = useState(false);
  const [medFormVisitId, setMedFormVisitId] = useState(null);
  const [medEditingId, setMedEditingId] = useState(null);
  const [medFormData, setMedFormData] = useState(emptyMedForm);
  const [medSubmitting, setMedSubmitting] = useState(false);
  const [medDeleteTarget, setMedDeleteTarget] = useState(null);
  const [medDeleteLoading, setMedDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredVisits(visits);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredVisits(
      visits.filter(
        (v) => getStudentName(v.student_id).toLowerCase().includes(term) || v.symptoms.toLowerCase().includes(term)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visits, searchTerm, students]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [visitsData, studentsData] = await Promise.all([
        healthService.listVisits(),
        studentsService.listStudents().catch(() => []),
      ]);
      setVisits(Array.isArray(visitsData) ? visitsData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : studentsData?.items || studentsData?.students || []);
    } catch (error) {
      toast.error('Failed to load clinic visits');
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find((s) => s.student_id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : studentId;
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (visit) => {
    setEditingId(visit.id);
    setFormData({
      student_id: visit.student_id,
      visit_date: visit.visit_date,
      symptoms: visit.symptoms,
      treatment_given: visit.treatment_given,
      referred_out: visit.referred_out,
      referred_to: visit.referred_to || '',
      attended_by: visit.attended_by || '',
      notes: visit.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student_id || !formData.symptoms || !formData.treatment_given) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        const { student_id, ...rest } = formData;
        await healthService.updateVisit(editingId, rest);
        toast.success('Clinic visit updated');
      } else {
        await healthService.createVisit(formData);
        toast.success('Clinic visit logged');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save clinic visit');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await healthService.deleteVisit(deleteTarget);
      toast.success('Clinic visit deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete clinic visit');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleApprove = async (visit) => {
    try {
      await healthService.approveVisit(visit.id);
      toast.success('Clinic visit approved');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to approve clinic visit');
    }
  };

  const handleReject = (visit) => {
    setPromptDialog({
      title: 'Reject Clinic Visit',
      message: 'Reason for rejecting this visit (optional):',
      defaultValue: '',
      onSubmit: async (reasonInput) => {
        const reason = reasonInput || undefined;
        try {
          await healthService.rejectVisit(visit.id, reason);
          toast.success('Clinic visit rejected');
          loadData();
        } catch (error) {
          toast.error(error.message || 'Failed to reject clinic visit');
        }
      },
    });
  };

  const toggleExpandVisit = async (visitId) => {
    if (expandedVisitId === visitId) {
      setExpandedVisitId(null);
      return;
    }
    setExpandedVisitId(visitId);
    if (!medicationsByVisit[visitId]) {
      setLoadingMedications(true);
      try {
        const meds = await healthService.listMedications(visitId);
        setMedicationsByVisit((prev) => ({ ...prev, [visitId]: Array.isArray(meds) ? meds : [] }));
      } catch (error) {
        toast.error('Failed to load medications for this visit');
      } finally {
        setLoadingMedications(false);
      }
    }
  };

  const reloadMedications = async (visitId) => {
    try {
      const meds = await healthService.listMedications(visitId);
      setMedicationsByVisit((prev) => ({ ...prev, [visitId]: Array.isArray(meds) ? meds : [] }));
    } catch (error) {
      toast.error('Failed to refresh medications');
    }
  };

  const openCreateMedModal = (visitId) => {
    setMedFormVisitId(visitId);
    setMedEditingId(null);
    setMedFormData(emptyMedForm);
    setShowMedModal(true);
  };

  const openEditMedModal = (visitId, med) => {
    setMedFormVisitId(visitId);
    setMedEditingId(med.id);
    setMedFormData({
      medication_name: med.medication_name,
      dose: med.dose,
      route: med.route,
      time_given: med.time_given ? med.time_given.slice(0, 16) : new Date().toISOString().slice(0, 16),
      administered_by: med.administered_by,
      notes: med.notes || '',
    });
    setShowMedModal(true);
  };

  const handleMedSubmit = async (e) => {
    e.preventDefault();
    if (!medFormData.medication_name || !medFormData.dose || !medFormData.administered_by) {
      toast.error('Please fill in all required fields');
      return;
    }
    setMedSubmitting(true);
    try {
      const payload = { ...medFormData, time_given: new Date(medFormData.time_given).toISOString() };
      if (medEditingId) {
        await healthService.updateMedication(medEditingId, payload);
        toast.success('Medication record updated');
      } else {
        await healthService.createMedication(medFormVisitId, payload);
        toast.success('Medication administration logged');
      }
      setShowMedModal(false);
      await reloadMedications(medFormVisitId);
    } catch (error) {
      toast.error(error.message || 'Failed to save medication record');
    } finally {
      setMedSubmitting(false);
    }
  };

  const confirmMedDelete = async () => {
    if (!medDeleteTarget) return;
    setMedDeleteLoading(true);
    try {
      await healthService.deleteMedication(medDeleteTarget.id);
      toast.success('Medication record deleted');
      await reloadMedications(medDeleteTarget.visitId);
      setMedDeleteTarget(null);
    } catch (error) {
      toast.error('Failed to delete medication record');
    } finally {
      setMedDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading clinic visits...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Health — Clinic Visits</h1>
            <p>Sick-bay visit log</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Log Clinic Visit
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search by student or symptoms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-container">
          {filteredVisits.length === 0 ? (
            <div className="empty-state">
              <p>No clinic visits found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Student</th>
                  <th>Date</th>
                  <th>Symptoms</th>
                  <th>Treatment</th>
                  <th>Referred Out</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVisits.map((visit) => (
                  <React.Fragment key={visit.id}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          className="btn-icon"
                          title="Medications"
                          onClick={() => toggleExpandVisit(visit.id)}
                        >
                          {expandedVisitId === visit.id ? <CaretDown size={16} /> : <CaretRight size={16} />}
                        </button>
                      </td>
                      <td>{getStudentName(visit.student_id)}</td>
                      <td>{visit.visit_date}</td>
                      <td>{visit.symptoms}</td>
                      <td>{visit.treatment_given}</td>
                      <td>{visit.referred_out ? `Yes (${visit.referred_to || '-'})` : 'No'}</td>
                      <td>
                        {visit.approval_status === 'pending' && <span className="badge inactive">Pending Approval</span>}
                        {visit.approval_status === 'approved' && <span className="badge active">Approved</span>}
                        {visit.approval_status === 'rejected' && (
                          <span className="badge" style={{ background: '#fee2e2', color: '#dc2626' }} title={visit.rejection_reason || ''}>Rejected</span>
                        )}
                      </td>
                      <td className="action-buttons">
                        {visit.approval_status === 'pending' && visit.created_by !== user?.id && (
                          <>
                            <button className="btn-icon" title="Approve" onClick={() => handleApprove(visit)}>
                              <Check size={18} color="#16a34a" />
                            </button>
                            <button className="btn-icon btn-danger" title="Reject" onClick={() => handleReject(visit)}>
                              <XIcon size={18} />
                            </button>
                          </>
                        )}
                        <button className="btn-icon" title="Edit" onClick={() => openEditModal(visit)}>
                          <PencilSimple size={18} />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          title="Delete"
                          onClick={() => setDeleteTarget(visit.id)}
                        >
                          <Trash size={18} />
                        </button>
                      </td>
                    </tr>
                    {expandedVisitId === visit.id && (
                      <tr>
                        <td></td>
                        <td colSpan={7} style={{ background: '#fafafa', padding: '10px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Pill size={16} /> Medications Administered
                            </strong>
                            <button className="btn btn-secondary" onClick={() => openCreateMedModal(visit.id)}>
                              <Plus size={16} /> Add Medication
                            </button>
                          </div>
                          {loadingMedications && !medicationsByVisit[visit.id] ? (
                            <p>Loading medications...</p>
                          ) : (medicationsByVisit[visit.id] || []).length === 0 ? (
                            <p style={{ color: '#666' }}>No medications logged for this visit</p>
                          ) : (
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Medication</th>
                                  <th>Dose</th>
                                  <th>Route</th>
                                  <th>Time Given</th>
                                  <th>Administered By</th>
                                  <th>Notes</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {medicationsByVisit[visit.id].map((med) => (
                                  <tr key={med.id}>
                                    <td>{med.medication_name}</td>
                                    <td>{med.dose}</td>
                                    <td className="text-capitalize">{med.route}</td>
                                    <td>{med.time_given ? new Date(med.time_given).toLocaleString() : '-'}</td>
                                    <td>{med.administered_by}</td>
                                    <td>{med.notes || '-'}</td>
                                    <td className="action-buttons">
                                      <button className="btn-icon" title="Edit" onClick={() => openEditMedModal(visit.id, med)}>
                                        <PencilSimple size={16} />
                                      </button>
                                      <button
                                        className="btn-icon btn-danger"
                                        title="Delete"
                                        onClick={() => setMedDeleteTarget({ id: med.id, visitId: visit.id })}
                                      >
                                        <Trash size={16} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Clinic Visit' : 'Log Clinic Visit'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {!editingId && (
                <div className="form-group">
                  <label>Student *</label>
                  <select value={formData.student_id} onChange={(e) => setFormData({ ...formData, student_id: e.target.value })} required>
                    <option value="">Select Student</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.student_id}>{s.first_name} {s.last_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Visit Date *</label>
                <input type="date" value={formData.visit_date} onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })} required />
              </div>

              <div className="form-group">
                <label>Symptoms *</label>
                <textarea value={formData.symptoms} onChange={(e) => setFormData({ ...formData, symptoms: e.target.value })} required />
              </div>

              <div className="form-group">
                <label>Treatment Given *</label>
                <textarea value={formData.treatment_given} onChange={(e) => setFormData({ ...formData, treatment_given: e.target.value })} required />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.referred_out}
                    onChange={(e) => setFormData({ ...formData, referred_out: e.target.checked })}
                    style={{ width: 'auto', marginRight: 8 }}
                  />
                  Referred out (e.g., to hospital)
                </label>
              </div>

              {formData.referred_out && (
                <div className="form-group">
                  <label>Referred To</label>
                  <input type="text" value={formData.referred_to} onChange={(e) => setFormData({ ...formData, referred_to: e.target.value })} placeholder="e.g., Korle Bu Hospital" />
                </div>
              )}

              <div className="form-group">
                <label>Attended By</label>
                <input type="text" value={formData.attended_by} onChange={(e) => setFormData({ ...formData, attended_by: e.target.value })} />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Log Visit'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMedModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{medEditingId ? 'Edit Medication Record' : 'Log Medication Administration'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowMedModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleMedSubmit}>
              <div className="form-group">
                <label>Medication Name *</label>
                <input
                  type="text"
                  value={medFormData.medication_name}
                  onChange={(e) => setMedFormData({ ...medFormData, medication_name: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Dose *</label>
                  <input
                    type="text"
                    value={medFormData.dose}
                    onChange={(e) => setMedFormData({ ...medFormData, dose: e.target.value })}
                    placeholder="e.g., 500mg"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Route *</label>
                  <select value={medFormData.route} onChange={(e) => setMedFormData({ ...medFormData, route: e.target.value })} required>
                    {MED_ROUTES.map((r) => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Time Given *</label>
                <input
                  type="datetime-local"
                  value={medFormData.time_given}
                  onChange={(e) => setMedFormData({ ...medFormData, time_given: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Administered By *</label>
                <input
                  type="text"
                  value={medFormData.administered_by}
                  onChange={(e) => setMedFormData({ ...medFormData, administered_by: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={medFormData.notes} onChange={(e) => setMedFormData({ ...medFormData, notes: e.target.value })} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={medSubmitting}>
                  {medSubmitting ? 'Saving...' : medEditingId ? 'Update' : 'Log Medication'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowMedModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Clinic Visit"
        message="This will permanently delete this clinic visit record. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!medDeleteTarget}
        onClose={() => setMedDeleteTarget(null)}
        title="Delete Medication Record"
        message="This will permanently delete this medication administration record. Continue?"
        type="warning"
        isLoading={medDeleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmMedDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="warning"
        input={{ placeholder: 'Reason (optional)', defaultValue: promptDialog?.defaultValue || '' }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default ClinicVisitsPage;
